import request from './config'
import { patientApi } from './patient'

/**
 * 睡眠日记相关API
 */
export const sleepDiaryApi = {
  /**
   * 分页查询医生绑定所有患者的睡眠日记列表
   * 简要描述：分页查询患者睡眠日记列表，支持按患者姓名、填写时间范围、医生姓名进行筛选
   * 请求方式：GET
   * 请求URL：/sleep-diary/page
   * @param {Object} params - 查询参数
   * @param {number} params.current - 当前页码，默认1（必选）
   * @param {number} params.size - 每页大小，默认10（必选）
   * @param {string} [params.name] - 患者姓名，支持模糊查询（必选，但可为空字符串）
   * @param {string} [params.phone] - 患者手机号码，支持模糊查询（必选，但可为空字符串）
   * @param {string} [params.startTime] - 填写时间范围-开始时间，格式：YYYY-MM-DD（必选，但可为空字符串）
   * @param {string} [params.endTime] - 填写时间范围-结束时间，格式：YYYY-MM-DD（必选，但可为空字符串）
   * @returns {Promise<Object>} 返回格式化的睡眠日记数据和分页信息
   */
  async getDoctorSleepDiaryList(params = {}) {
    try {
      console.log('获取医生绑定患者睡眠日记列表，请求参数:', params)

      // 构建请求参数
      const { current = 1, size = 10, ...searchParams } = params

      // 根据接口文档，所有参数都作为查询参数传递
      const queryParams = {
        current,
        size
      }

      // 添加搜索条件到查询参数
      if (searchParams.name) {
        queryParams.name = searchParams.name
      }
      if (searchParams.phone) {
        queryParams.phone = searchParams.phone
      }
      if (searchParams.startTime) {
        queryParams.startTime = searchParams.startTime
      }
      if (searchParams.endTime) {
        queryParams.endTime = searchParams.endTime
      }

      console.log('发送请求到 /sleep-diary/page')
      console.log('查询参数:', queryParams)

      // 发送GET请求，所有参数作为查询参数传递
      const response = await request.get('/sleep-diary/page', {
        params: queryParams
      })

      console.log('睡眠日记列表API原始响应:', response)

      const data = response.data || response

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        // 确保返回的数据格式符合前端需要
        const records = data.data?.records || []
        const total = data.data?.total || 0

        console.log('原始睡眠日记数据条数:', records.length)
        console.log('总记录数:', total)

        if (records.length === 0) {
          console.log('当前查询条件下没有睡眠日记数据')
          return {
            success: true,
            message: data.message || '查询成功，暂无数据',
            data: [],
            total: 0,
            current: current,
            size: size
          }
        }

        // 获取所有唯一的患者ID
        const patientIds = [...new Set(records.map(diary => diary.patientId).filter(Boolean))]
        console.log('需要获取患者信息的ID列表:', patientIds)

        // 批量获取患者信息
        const patientMap = {}
        if (patientIds.length > 0) {
          try {
            // 调用患者列表接口获取患者信息
            const patientResult = await patientApi.getDoctorPatientList({
              current: 1,
              size: 1000 // 获取足够多的患者信息
            })

            if (patientResult.success && patientResult.data) {
              // 创建患者ID到患者信息的映射
              patientResult.data.forEach(patient => {
                if (patient.id) {
                  patientMap[patient.id] = patient
                }
              })
              console.log('患者信息映射创建完成:', Object.keys(patientMap).length, '个患者')
            }
          } catch (error) {
            console.warn('获取患者信息失败:', error)
            // 即使获取患者信息失败，也继续处理睡眠日记数据
          }
        }

        // 转换数据格式以适配前端显示
        const formattedDiaries = records.map((diary, index) => {
          console.log(`处理第${index + 1}个睡眠日记:`, diary)

          // 格式化时间显示
          const formatDateTime = (dateTimeStr) => {
            if (!dateTimeStr) return ''
            try {
              const date = new Date(dateTimeStr)
              return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            } catch (e) {
              return dateTimeStr
            }
          }

          // 格式化日期显示
          const formatDate = (dateTimeStr) => {
            if (!dateTimeStr) return ''
            try {
              const date = new Date(dateTimeStr)
              return date.toLocaleDateString('zh-CN')
            } catch (e) {
              return dateTimeStr
            }
          }

          // 格式化时长显示（将时间格式转换为分钟）
          const formatDurationToMinutes = (timeStr) => {
            if (!timeStr) return 0
            try {
              const [hours, minutes, seconds] = timeStr.split(':').map(Number)
              return hours * 60 + minutes + (seconds > 0 ? 1 : 0) // 秒数大于0时向上取整
            } catch (e) {
              return 0
            }
          }

          // 获取患者信息
          const patient = patientMap[diary.patientId]
          const patientName = patient ? patient.name : `患者${diary.patientId}`

          console.log(`处理患者ID ${diary.patientId}:`, patient ? `找到患者 ${patient.name}` : '未找到患者信息')

          return {
            id: diary.id,
            patientId: diary.patientId,
            date: formatDate(diary.fillTime), // 填写日期
            patientName: patientName, // 患者姓名
            bedTime: formatDateTime(diary.bedtime), // 上床时间
            fallAsleepTime: formatDateTime(diary.fallAsleepTime), // 入睡时间
            wakeUpTime: formatDateTime(diary.wakeUpTime), // 起床时间
            awakeTime: formatDateTime(diary.awakeTime), // 最终醒来时间

            // 睡眠质量相关
            isAwakeAtNight: diary.isAwakeAtNight, // 夜间是否醒来
            awakeTimes: diary.awakeTimes || 0, // 夜醒次数
            awakeDuration: diary.awakeDuration, // 夜醒总时长
            fallAsleepDuration: diary.fallAsleepDuration, // 入睡潜伏期
            sleepFeeling: diary.sleepFeeling || '', // 睡眠感受

            // 午睡相关
            isNapYesterday: diary.isNapYesterday, // 是否午睡
            napStartTime: diary.napStartTime ? formatDateTime(diary.napStartTime) : '',
            napEndTime: diary.napEndTime ? formatDateTime(diary.napEndTime) : '',

            // 用药相关
            isTakeMedicineYesterday: diary.isTakeMedicineYesterday, // 是否服药
            medicineName: diary.medicineName || diary.medicineId || '', // 药品名称（兼容旧字段）
            dosage: diary.dosage || '',

            // 咖啡因相关
            is_caffeine_drink: diary.is_caffeine_drink || 0, // 是否饮用含咖啡因饮品
            caffeine_amount: diary.caffeine_amount || '', // 咖啡因饮用量
            caffeine_drink_time: diary.caffeine_drink_time ? formatDateTime(diary.caffeine_drink_time) : '', // 咖啡因饮用时间

            // 酒精相关
            is_alcohol_drink: diary.is_alcohol_drink || 0, // 是否饮用酒精饮品
            alcohol_amount: diary.alcohol_amount || '', // 酒精饮用量
            alcohol_drink_time: diary.alcohol_drink_time ? formatDateTime(diary.alcohol_drink_time) : '', // 酒精饮用时间

            // 其他生活习惯
            isExercise: diary.isExercise || 0, // 是否运动
            isRelaxationTraining: diary.isRelaxationTraining || 0, // 是否放松训练
            relaxationStartTime: diary.relaxationStartTime ? formatDateTime(diary.relaxationStartTime) : '',
            relaxationEndTime: diary.relaxationEndTime ? formatDateTime(diary.relaxationEndTime) : '',
            isMostSleepy: diary.isMostSleepy || 0, // 白天是否困倦
            mostSleepyTime: diary.mostSleepyTime ? formatDateTime(diary.mostSleepyTime) : '',

            fillTime: formatDateTime(diary.fillTime), // 填写时间
            doctorName: patient?.doctorName || '当前医生', // 医生姓名

            // 保留原始数据以便详情查看
            originalData: diary,
            // 保留患者完整信息
            patientInfo: patient
          }
        })

        console.log(`格式化睡眠日记数据完成，共${formattedDiaries.length}条记录`)

        return {
          success: true,
          message: data.message || data.msg || '获取睡眠日记列表成功',
          data: formattedDiaries,
          total: total,
          current: current,
          size: size
        }
      } else {
        // 获取失败
        const errorMessage = data.message || data.msg || '获取睡眠日记列表失败'
        console.warn('获取睡眠日记列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取睡眠日记列表异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试',
          data: [],
          total: 0
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录',
          data: [],
          total: 0
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.msg || error.message || '获取睡眠日记列表失败',
        data: [],
        total: 0
      }
    }
  },

  /**
    * 根据睡眠日记ID获取详细信息
    * 请求方式：GET
    * 请求URL：/sleep-diary/{id}
    * @param {number|string} id - 睡眠日记ID
    * @returns {Promise<Object>} 睡眠日记详细信息
    */
  async getSleepDiaryDetail(id) {
    try {
      console.log('=== 获取睡眠日记详情 ===')
      console.log('睡眠日记ID:', id)

      if (!id) {
        return {
          success: false,
          message: '睡眠日记ID不能为空',
          data: null
        }
      }

      // 发送GET请求获取睡眠日记详情
      const response = await request.get(`/sleep-diary/${id}`)

      console.log('睡眠日记详情API原始响应:', response)

      const data = response.data || response

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        const diaryDetail = data.data || {}

        console.log('睡眠日记详情获取成功:', diaryDetail)

        // 格式化时间显示
        // 支持多种时间格式：ISO 8601 (2025-12-08T22:01:00) 和 标准格式 (2025-12-08 22:01:00)
        const formatDateTime = (dateTimeStr) => {
          if (!dateTimeStr) return ''
          try {
            // 如果是标准格式 (YYYY-MM-DD HH:mm:ss)，需要转换为ISO格式
            let isoStr = dateTimeStr
            if (dateTimeStr.includes(' ') && !dateTimeStr.includes('T')) {
              isoStr = dateTimeStr.replace(' ', 'T')
            }
            const date = new Date(isoStr)
            // 检查日期是否有效
            if (isNaN(date.getTime())) {
              return dateTimeStr
            }
            return date.toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })
          } catch (e) {
            console.warn('时间格式化失败:', dateTimeStr, e)
            return dateTimeStr
          }
        }

        // 格式化时长显示
        const formatDuration = (timeStr) => {
          if (!timeStr) return ''
          return timeStr
        }

        // 转换是否字段为中文显示
        const formatYesNo = (value) => {
          if (value === 1 || value === '1') return '是'
          if (value === 0 || value === '0') return '否'
          return value || '未知'
        }

        // 格式化详情数据以便前端显示
        const formattedDetail = {
          // 基础信息
          id: diaryDetail.id,
          patientId: diaryDetail.patientId,
          fillTime: formatDateTime(diaryDetail.fillTime),

          // 睡眠时间相关
          bedtime: formatDateTime(diaryDetail.bedtime),
          fallAsleepTime: formatDateTime(diaryDetail.fallAsleepTime),
          wakeUpTime: formatDateTime(diaryDetail.wakeUpTime),
          awakeTime: formatDateTime(diaryDetail.awakeTime),
          fallAsleepDuration: formatDuration(diaryDetail.fallAsleepDuration),

          // 夜醒相关
          isAwakeAtNight: formatYesNo(diaryDetail.isAwakeAtNight),
          awakeTimes: diaryDetail.awakeTimes || 0,
          awakeDuration: formatDuration(diaryDetail.awakeDuration),

          // 午睡相关
          isNapYesterday: formatYesNo(diaryDetail.isNapYesterday),
          napStartTime: formatDateTime(diaryDetail.napStartTime),
          napEndTime: formatDateTime(diaryDetail.napEndTime),

          // 用药相关
          isTakeMedicineYesterday: formatYesNo(diaryDetail.isTakeMedicineYesterday),
          medicineName: diaryDetail.medicineName || diaryDetail.medicineId || '', // 药品名称（兼容旧字段）
          dosage: diaryDetail.dosage || '',

          // 睡眠感受
          sleepFeeling: diaryDetail.sleepFeeling || '',

          // 咖啡因相关（兼容驼峰和下划线两种命名）
          isCaffeineDrink: formatYesNo(diaryDetail.isCaffeineDrink ?? diaryDetail.is_caffeine_drink),
          caffeineAmount: diaryDetail.caffeineAmount || diaryDetail.caffeine_amount || '',
          caffeineDrinkTime: formatDateTime(diaryDetail.caffeineDrinkTime || diaryDetail.caffeine_drink_time),
          // 同时保留下划线命名的字段以兼容旧代码
          is_caffeine_drink: formatYesNo(diaryDetail.isCaffeineDrink ?? diaryDetail.is_caffeine_drink),
          caffeine_amount: diaryDetail.caffeineAmount || diaryDetail.caffeine_amount || '',
          caffeine_drink_time: formatDateTime(diaryDetail.caffeineDrinkTime || diaryDetail.caffeine_drink_time),

          // 酒精相关（兼容驼峰和下划线两种命名）
          isAlcoholDrink: formatYesNo(diaryDetail.isAlcoholDrink ?? diaryDetail.is_alcohol_drink),
          alcoholAmount: diaryDetail.alcoholAmount || diaryDetail.alcohol_amount || '',
          alcoholDrinkTime: formatDateTime(diaryDetail.alcoholDrinkTime || diaryDetail.alcohol_drink_time),
          // 同时保留下划线命名的字段以兼容旧代码
          is_alcohol_drink: formatYesNo(diaryDetail.isAlcoholDrink ?? diaryDetail.is_alcohol_drink),
          alcohol_amount: diaryDetail.alcoholAmount || diaryDetail.alcohol_amount || '',
          alcohol_drink_time: formatDateTime(diaryDetail.alcoholDrinkTime || diaryDetail.alcohol_drink_time),

          // 放松训练相关
          isRelaxationTraining: formatYesNo(diaryDetail.isRelaxationTraining),
          relaxationStartTime: formatDateTime(diaryDetail.relaxationStartTime),
          relaxationEndTime: formatDateTime(diaryDetail.relaxationEndTime),

          // 困倦相关
          isMostSleepy: formatYesNo(diaryDetail.isMostSleepy),
          mostSleepyTime: formatDateTime(diaryDetail.mostSleepyTime),

          // 运动相关
          isExercise: formatYesNo(diaryDetail.isExercise),

          // 保留原始数据
          originalData: diaryDetail
        }

        return {
          success: true,
          message: data.message || data.msg || '获取睡眠日记详情成功',
          data: formattedDetail
        }
      } else {
        // 获取失败
        const errorMessage = data.message || data.msg || '获取睡眠日记详情失败'
        console.warn('获取睡眠日记详情失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('获取睡眠日记详情异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试',
          data: null
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录',
          data: null
        }
      }

      // 检查是否是权限错误
      if (error.response?.status === 403) {
        return {
          success: false,
          message: '没有权限查看该睡眠日记详情',
          data: null
        }
      }

      // 检查是否是数据不存在
      if (error.response?.status === 404) {
        return {
          success: false,
          message: '睡眠日记不存在或已被删除',
          data: null
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.msg || error.message || '获取睡眠日记详情失败',
        data: null
      }
    }
  },

  /**
   * 医生编辑患者睡眠日记
   * 简要描述：医生编辑患者睡眠日记
   * 请求方式：PUT
   * 请求URL：/sleep-diary/doctor/edit
   * @param {Object} diaryData - 睡眠日记数据
   * @returns {Promise<Object>} 编辑结果
   */
  async editSleepDiary(diaryData) {
    try {
      console.log('=== 医生编辑患者睡眠日记 ===')
      console.log('原始编辑数据:', diaryData)

      if (!diaryData || !diaryData.id || !diaryData.patientId) {
        console.error('必要字段缺失:', { id: diaryData?.id, patientId: diaryData?.patientId })
        return {
          success: false,
          message: '睡眠日记ID和患者ID不能为空',
          data: null
        }
      }

      // 格式化时间字段为 YYYY-MM-DD HH:mm:ss 格式
      const formatToStandard = (dateTimeStr) => {
        if (!dateTimeStr) return ''
        try {
          // 如果已经是标准格式 (YYYY-MM-DD HH:mm:ss)，直接返回
          if (dateTimeStr.includes(' ') && !dateTimeStr.includes('T')) {
            // 确保秒数存在
            if (dateTimeStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)) {
              return dateTimeStr + ':00'
            }
            return dateTimeStr
          }
          // 如果是ISO格式 (YYYY-MM-DDTHH:mm:ss)，转换为标准格式
          if (dateTimeStr.includes('T')) {
            return dateTimeStr.replace('T', ' ')
          }
          // 尝试解析为Date对象
          const date = new Date(dateTimeStr)
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const seconds = String(date.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
          }
          return dateTimeStr
        } catch (e) {
          console.warn('时间格式化失败:', dateTimeStr, e)
          return dateTimeStr
        }
      }

      // 规范化时间（空值返回null，有值则格式化为 YYYY-MM-DD HH:mm:ss）
      const normalizeTime = (val) => {
        if (!val) return null
        return formatToStandard(val)
      }

      // 构建请求数据，严格按照接口文档格式（不强行覆盖默认值）
      const requestData = {
        id: parseInt(diaryData.id),
        patientId: parseInt(diaryData.patientId),
        bedtime: normalizeTime(diaryData.bedtime),
        wakeUpTime: normalizeTime(diaryData.wakeUpTime),
        fallAsleepTime: normalizeTime(diaryData.fallAsleepTime),
        awakeTime: normalizeTime(diaryData.awakeTime),
        isAwakeAtNight: diaryData.isAwakeAtNight ?? (diaryData.awakeTimes > 0 ? 1 : 0),
        awakeTimes: parseInt(diaryData.awakeTimes) || 0,
        awakeDuration: diaryData.awakeDuration || '00:00:00',
        isNapYesterday: diaryData.isNapYesterday ?? 0,
        napStartTime: diaryData.isNapYesterday === 1 ? normalizeTime(diaryData.napStartTime) : null,
        napEndTime: diaryData.isNapYesterday === 1 ? normalizeTime(diaryData.napEndTime) : null,
        sleepFeeling: diaryData.sleepFeeling || '',
        // 咖啡因相关字段 - 直接使用前端传递的值
        isCaffeineDrink: diaryData.isCaffeineDrink !== undefined ? parseInt(diaryData.isCaffeineDrink) : (diaryData.is_caffeine_drink !== undefined ? parseInt(diaryData.is_caffeine_drink) : 0),
        caffeineAmount: diaryData.isCaffeineDrink === 1 || diaryData.is_caffeine_drink === 1
          ? (diaryData.caffeineAmount ?? diaryData.caffeine_amount ?? '')
          : null,
        caffeineDrinkTime: diaryData.isCaffeineDrink === 1 || diaryData.is_caffeine_drink === 1
          ? normalizeTime(diaryData.caffeineDrinkTime || diaryData.caffeine_drink_time)
          : null,
        // 酒精相关字段 - 直接使用前端传递的值
        isAlcoholDrink: diaryData.isAlcoholDrink !== undefined ? parseInt(diaryData.isAlcoholDrink) : (diaryData.is_alcohol_drink !== undefined ? parseInt(diaryData.is_alcohol_drink) : 0),
        alcoholAmount: diaryData.isAlcoholDrink === 1 || diaryData.is_alcohol_drink === 1
          ? (diaryData.alcoholAmount ?? diaryData.alcohol_amount ?? '')
          : null,
        alcoholDrinkTime: diaryData.isAlcoholDrink === 1 || diaryData.is_alcohol_drink === 1
          ? normalizeTime(diaryData.alcoholDrinkTime || diaryData.alcohol_drink_time)
          : null,
        // 最困倦时刻字段 - 直接使用前端传递的值
        isMostSleepy: diaryData.isMostSleepy !== undefined ? parseInt(diaryData.isMostSleepy) : 0,
        mostSleepyTime: diaryData.isMostSleepy === 1 ? normalizeTime(diaryData.mostSleepyTime) : null,
        fillTime: normalizeTime(diaryData.fillTime) || (() => {
          const now = new Date()
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, '0')
          const day = String(now.getDate()).padStart(2, '0')
          const hours = String(now.getHours()).padStart(2, '0')
          const minutes = String(now.getMinutes()).padStart(2, '0')
          const seconds = String(now.getSeconds()).padStart(2, '0')
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        })(),
        fallAsleepDuration: diaryData.fallAsleepDuration || '00:00:00'
      }

      console.log('=== 编辑睡眠日记 - 原始数据 ===')
      console.log('原始diaryData:', diaryData)
      console.log('=== 编辑睡眠日记 - 处理后的请求数据 ===')
      console.log('提交到 /sleep-diary/doctor/edit 的数据:', JSON.stringify(requestData, null, 2))
      console.log('关键字段值:')
      console.log('  isCaffeineDrink:', requestData.isCaffeineDrink, 'caffeineAmount:', requestData.caffeineAmount, 'caffeineDrinkTime:', requestData.caffeineDrinkTime)
      console.log('  isAlcoholDrink:', requestData.isAlcoholDrink, 'alcoholAmount:', requestData.alcoholAmount, 'alcoholDrinkTime:', requestData.alcoholDrinkTime)
      console.log('  isMostSleepy:', requestData.isMostSleepy, 'mostSleepyTime:', requestData.mostSleepyTime)

      const response = await request.put('/sleep-diary/doctor/edit', requestData)
      console.log('编辑睡眠日记API响应:', response)

      const data = response.data || response

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        console.log('✅ 睡眠日记编辑成功')
        return {
          success: true,
          message: data.message || '编辑成功',
          data: data.data
        }
      } else {
        console.error('❌ 编辑睡眠日记失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '编辑失败',
          data: null
        }
      }
    } catch (error) {
      console.error('编辑睡眠日记异常:', error)

      let errorMessage = '编辑睡眠日记失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage,
        data: null
      }
    }
  },

  // 简化的时长格式化函数
  formatDurationSimple(durationStr) {
    if (!durationStr) return '00:00:00'

    // 如果是数字（分钟）
    if (/^\d+$/.test(durationStr)) {
      const minutes = parseInt(durationStr)
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`
    }

    // 如果已经是时间格式
    if (durationStr.includes(':')) {
      const parts = durationStr.split(':')
      if (parts.length === 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`
      } else if (parts.length === 3) {
        return durationStr
      }
    }

    return '00:00:00'
  }
}

export default sleepDiaryApi
